import { refusal, type HostProvider, type Refusal } from '@tasks/bridge-client/host';
import { type TokenPair, type VittoIdentity, vittoSessionSchema } from '@tasks/contracts/auth';
import { ApiError } from '#lib/api-client';

/**
 * TASKS-APP-FULL-UI-1 U2b — THE DOOR: the host's single-use pass in, the product's own session out.
 *
 *   host.grants(reason) ⇒ the pass   (embedded: INIT/REFRESH from Vitto; standalone: the redeemed PKCE code)
 *   POST /api/v1/auth/vitto {grant}  ⇒ { accessToken, refreshToken, accessExpiresAt, identity }   (BABAS, contract 06-10)
 *   session.accept(pair)             ⇒ from here `apps/web` works with its ordinary Bearer and its ordinary refresh
 *
 * Never loops: ONE pass ⇒ at most one retry, and only for the two server answers that say the pass was NOT spent (503
 * `replay_unprotected` / `jwks_unavailable`: «the pass is retried with the SAME pass»). A pass the server refused is not asked again —
 * only the person (or the host, on a renewal) can bring a new one. The answer is validated against the contract before anything is
 * accepted: a malformed pair is never a session.
 */
export type DoorReason = 'open' | 'renew';

export type DoorFailure =
  /** the HOST said no (or did not answer): the closed list of the wire, or `timeout` */
  | { kind: 'host'; reason: Refusal | 'timeout' }
  /** the product refused the pass: `code` is its word (replayed, expired, app_mismatch, installation_org_mismatch, user_inactive…) */
  | { kind: 'rejected'; status: number; code: string | null }
  /** nobody could answer (network, 5xx, an answer that is not the contract), after the one retry that is allowed */
  | { kind: 'unavailable'; status: number | null };

export type DoorOutcome =
  | { ok: true; identity: VittoIdentity }
  | { ok: false; failure: DoorFailure };

/** 503s that say «the pass was not spent» (BABAS: `replay_unprotected`, `jwks_unavailable`): the one case where the SAME pass is sent again. */
const NOT_SPENT = new Set(['replay_unprotected', 'jwks_unavailable']);
const RETRY_AFTER_MS = 500;

const codeOf = (error: ApiError): string | null => {
  const code: unknown = error.problem['code'];
  return typeof code === 'string' ? code : null;
};

export async function enterWithVitto(deps: {
  host: Pick<HostProvider, 'grants' | 'roster'>;
  exchange: (grant: string, roster?: string) => Promise<unknown>;
  accept: (pair: TokenPair) => void;
  /**
   * Asked with the identity the product returned BEFORE `accept`: false ⇒ the pair is NEVER accepted (the door says `account_changed`).
   * The gate uses it to refuse a renewal for another account without that account's token ever becoming the session of the mounted tree.
   */
  admit?: (identity: VittoIdentity) => boolean;
  reason: DoorReason;
  wait?: (ms: number) => Promise<void>;
}): Promise<DoorOutcome> {
  const wait =
    deps.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let grant: string;
  try {
    grant = await deps.host.grants(deps.reason);
  } catch (error) {
    const word = error instanceof Error ? error.message : '';
    return {
      ok: false,
      failure: { kind: 'host', reason: word === 'timeout' ? 'timeout' : refusal(word) },
    };
  }

  let answer: unknown;
  for (let attempt = 0; ; attempt += 1) {
    try {
      // the host's SIGNED roster (if it sent one) travels with the grant, as it came; none ⇒ the exchange gets the grant alone, as before
      const roster = deps.host.roster?.();
      answer =
        roster === undefined ? await deps.exchange(grant) : await deps.exchange(grant, roster);
      break;
    } catch (error) {
      if (!(error instanceof ApiError))
        return { ok: false, failure: { kind: 'unavailable', status: null } };
      const { status } = error.problem;
      const code = codeOf(error);
      if (status === 503 && code !== null && NOT_SPENT.has(code) && attempt === 0) {
        await wait(RETRY_AFTER_MS);
        continue;
      }
      if (status >= 500) return { ok: false, failure: { kind: 'unavailable', status } };
      return { ok: false, failure: { kind: 'rejected', status, code } };
    }
  }

  const parsed = vittoSessionSchema.safeParse(answer);
  if (!parsed.success) return { ok: false, failure: { kind: 'unavailable', status: null } };
  const { identity, ...pair } = parsed.data;
  if (deps.admit?.(identity) === false)
    return { ok: false, failure: { kind: 'rejected', status: 0, code: 'account_changed' } };
  deps.accept(pair);
  return { ok: true, identity };
}
